import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { EnvelopeSimple, Phone, WhatsappLogo, MapPin, ArrowRight, CheckCircle } from "@phosphor-icons/react";
import { submitContact } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Section, PageHero, EyebrowLabel } from "@/components/marketing/MarketingUI";
import useSEO from "@/lib/useSEO";

const INITIAL = {
  name: "", email: "", company: "", phone: "", subject: "Enquiry", message: "",
};

/* Known deep-link topics — map slug → pre-filled subject line so users
 * landing here from an in-app CTA see the right context immediately.
 * Unknown topics are still accepted (used verbatim as the subject) so we
 * don't drop signal from a slightly-misspelled param. */
const TOPIC_SUBJECTS = {
  "password-reset": "Password reset request",
  "demo":           "Request a demo",
  "pricing":        "Pricing enquiry",
  "partnership":    "Partnership enquiry",
  "careers":        "Careers enquiry",
  "support":        "Support request",
};

const TOPIC_MESSAGES = {
  "password-reset": "I've been locked out of my SRES workspace and need my administrator to issue a temporary password.\n\nMy account email: \nCompany / workspace: \n",
};

export default function ContactPage() {
  useSEO({
    title: "Contact · Talk to a geologist, not a chatbot",
    description: "Reach the SRES team at info@sresources.co · +91 80799 89999 · WhatsApp +91 99388 72207. Every enquiry answered within one business day.",
    keywords: "SRES contact, info@sresources.co, resource estimation demo",
    path: "/contact",
  });
  const [sp] = useSearchParams();
  const [form, setForm] = useState(INITIAL);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  // Pre-fill the subject (and optionally the message body) whenever the
  // user lands with a ?topic= query param. Fires exactly once per topic.
  useEffect(() => {
    const topic = (sp.get("topic") || "").trim().toLowerCase();
    if (!topic) return;
    const subject = TOPIC_SUBJECTS[topic] || topic.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
    const message = TOPIC_MESSAGES[topic];
    setForm((f) => ({
      ...f,
      subject,
      message: message && !f.message ? message : f.message,
    }));
  }, [sp]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.message.trim().length < 8) {
      toast.error("Please write at least a few sentences about how we can help.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await submitContact({ ...form, source: "contact-page" });
      setDone(res);
      setForm(INITIAL);
      toast.success("Message received — we will be in touch shortly.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not submit — please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <PageHero
        eyebrow="Contact"
        title="Talk to a geologist, not a chatbot."
        description="We reply to every serious enquiry within one business day. Share a bit about your project — we will bring the right specialist to the first call."
        testid="contact-hero"
      />

      <Section className="py-20 md:py-24" testid="contact-body">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr,1.2fr] gap-12">
          <div>
            <EyebrowLabel className="mb-5">Direct lines</EyebrowLabel>
            <div className="space-y-5 text-slate-800">
              <ContactRow
                icon={EnvelopeSimple}
                label="Email"
                value="info@sresources.co"
                href="mailto:info@sresources.co"
                testid="contact-email"
              />
              <ContactRow
                icon={Phone}
                label="Phone"
                value="+91 80799 89999"
                href="tel:+918079989999"
                testid="contact-phone"
              />
              <ContactRow
                icon={WhatsappLogo}
                label="WhatsApp"
                value="+91 99388 72207"
                href="https://wa.me/919938872207"
                testid="contact-whatsapp"
              />
              <ContactRow
                icon={MapPin}
                label="Office locations"
                value="Bengaluru · Bhubaneswar · India"
                testid="contact-office"
              />
            </div>

            <div className="mt-10 p-6 rounded-xl bg-brand-ink text-white">
              <div className="text-[11px] uppercase tracking-[0.28em] text-brand-blueLight">Response SLA</div>
              <div className="mt-2 font-heading font-black text-2xl leading-tight">One business day.</div>
              <p className="mt-3 text-white/70 text-[14px] leading-relaxed">
                Every enquiry lands in a geologist&apos;s inbox — not a support queue. Complex questions get
                escalated to a senior resource specialist before we reply.
              </p>
            </div>
          </div>

          <div>
            {done ? (
              <div className="p-10 rounded-2xl border border-emerald-200 bg-emerald-50 text-emerald-900" data-testid="contact-success">
                <div className="flex items-center gap-3">
                  <CheckCircle size={28} weight="fill" className="text-emerald-600" />
                  <div className="font-heading font-black text-2xl">Message received</div>
                </div>
                <p className="mt-3 text-emerald-800/80 text-[15px] leading-relaxed">
                  {done.message} A member of the team will follow up on this inbox shortly.
                </p>
                <button
                  onClick={() => setDone(null)}
                  className="mt-6 inline-flex items-center gap-2 text-emerald-700 font-medium text-sm hover:underline"
                  data-testid="contact-send-another"
                >
                  Send another message <ArrowRight size={14} weight="bold" />
                </button>
              </div>
            ) : (
              <form
                onSubmit={submit}
                className="p-8 md:p-10 rounded-2xl border border-slate-200 bg-white"
                data-testid="contact-form"
              >
                <div className="text-[11px] uppercase tracking-[0.28em] text-brand-blue">Send a message</div>
                <div className="mt-2 font-heading font-black text-slate-900 text-2xl">Tell us about your project.</div>

                <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Field label="Your name" required>
                    <Input
                      value={form.name} onChange={set("name")}
                      required minLength={2}
                      placeholder="Full name"
                      className="h-11 border-slate-200 focus-visible:ring-brand-blue"
                      data-testid="contact-input-name"
                    />
                  </Field>
                  <Field label="Work email" required>
                    <Input
                      type="email" value={form.email} onChange={set("email")}
                      required
                      placeholder="name@company.com"
                      className="h-11 border-slate-200 focus-visible:ring-brand-blue"
                      data-testid="contact-input-email"
                    />
                  </Field>
                  <Field label="Company">
                    <Input
                      value={form.company} onChange={set("company")}
                      placeholder="e.g. Gold Valley Mining"
                      className="h-11 border-slate-200 focus-visible:ring-brand-blue"
                      data-testid="contact-input-company"
                    />
                  </Field>
                  <Field label="Phone">
                    <Input
                      value={form.phone} onChange={set("phone")}
                      placeholder="+91 XXXXX XXXXX"
                      className="h-11 border-slate-200 focus-visible:ring-brand-blue"
                      data-testid="contact-input-phone"
                    />
                  </Field>
                </div>
                <div className="mt-4">
                  <Field label="Subject" required>
                    <Input
                      value={form.subject} onChange={set("subject")}
                      required minLength={2}
                      placeholder="Resource estimation for a Fe project"
                      className="h-11 border-slate-200 focus-visible:ring-brand-blue"
                      data-testid="contact-input-subject"
                    />
                  </Field>
                </div>
                <div className="mt-4">
                  <Field label="How can we help?" required>
                    <Textarea
                      value={form.message} onChange={set("message")}
                      required minLength={8} rows={6}
                      placeholder="Tell us about your commodity, drilling programme size, and the reporting standard you are targeting."
                      className="border-slate-200 focus-visible:ring-brand-blue"
                      data-testid="contact-input-message"
                    />
                  </Field>
                </div>

                <div className="mt-6 flex items-center justify-between gap-4">
                  <div className="text-[12px] text-slate-500">
                    Submitting agrees to our <a href="/privacy" className="text-brand-blue hover:underline">Privacy Policy</a>.
                  </div>
                  <Button
                    type="submit" disabled={submitting}
                    className="h-11 px-6 bg-brand-blue hover:bg-brand-blueDeep text-white font-medium rounded-md"
                    data-testid="contact-submit"
                  >
                    {submitting ? "Sending…" : "Send message"}
                    {!submitting ? <ArrowRight size={14} weight="bold" className="ml-2" /> : null}
                  </Button>
                </div>
              </form>
            )}
          </div>
        </div>
      </Section>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <Label className="text-[11px] uppercase tracking-[0.18em] text-slate-500">
        {label} {required ? <span className="text-brand-blue">*</span> : null}
      </Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

function ContactRow({ icon: Icon, label, value, href, testid }) {
  const inner = (
    <div className="flex items-start gap-4 group">
      <div className="w-10 h-10 rounded-lg bg-brand-blue/10 text-brand-blue flex items-center justify-center shrink-0">
        <Icon size={18} weight="bold" />
      </div>
      <div>
        <div className="text-[11px] uppercase tracking-[0.22em] text-slate-500">{label}</div>
        <div className="mt-0.5 text-slate-900 text-[16px] font-medium group-hover:text-brand-blue transition-colors">{value}</div>
      </div>
    </div>
  );
  if (!href) return <div data-testid={testid}>{inner}</div>;
  return (
    <a href={href} target={href.startsWith("http") ? "_blank" : undefined} rel="noreferrer" data-testid={testid} className="block">
      {inner}
    </a>
  );
}
